import { z } from 'zod';
import { analyzeSms, isGsm7, toGreekGsmCapitals } from '../sms';
import { addressFirstName } from '../greek';
import { IsoDateTimeSchema, LocaleSchema, UuidSchema } from './common';
import { MAX_SMS_SEGMENTS } from './messaging';

/**
 * Review request lifecycle (ARCHITECTURE §8). Truth rules:
 *  - `google_clicked` means the recipient clicked the Google button. It is NEVER a posted review.
 *  - Both liked and disliked paths show the Google review button.
 */
export const REVIEW_REQUEST_STATUSES = [
  'created',
  'queued',
  'sent',
  'delivered',
  'opened',
  'liked',
  'disliked',
  'google_clicked',
  'feedback_submitted',
  'opted_out',
  'expired',
  'failed',
] as const;
export type ReviewRequestStatus = (typeof REVIEW_REQUEST_STATUSES)[number];

export const REVIEW_REQUEST_EVENT_TYPES = [
  'created',
  'queued',
  'sent',
  'delivered',
  'failed',
  'page_opened',
  'liked',
  'disliked',
  'google_clicked',
  'feedback_submitted',
  'opted_out',
  'reminder_scheduled',
  'reminder_sent',
  'reminders_stopped',
  'expired',
  'public_review_detected',
  'possible_match',
] as const;
export type ReviewRequestEventType = (typeof REVIEW_REQUEST_EVENT_TYPES)[number];

export const REVIEW_CHANNELS = ['sms', 'email'] as const;
export type ReviewChannel = (typeof REVIEW_CHANNELS)[number];

/** Store setting: which channel to use. `both_prefer_sms` = SMS if phone + consent, otherwise email. */
/** Display order everywhere the choice is offered: the recommended default first. */
export const REVIEW_CHANNEL_PREFERENCES = ['both_prefer_sms', 'sms', 'email'] as const;
export type ReviewChannelPreference = (typeof REVIEW_CHANNEL_PREFERENCES)[number];

export const REVIEW_SENTIMENTS = ['liked', 'disliked'] as const;
export type ReviewSentiment = (typeof REVIEW_SENTIMENTS)[number];

export const REVIEW_REQUEST_SOURCES = ['manual', 'auto', 'automation', 'campaign'] as const;
export type ReviewRequestSource = (typeof REVIEW_REQUEST_SOURCES)[number];

export const REMINDER_STOP_REASONS = [
  'google_clicked',
  'feedback_submitted',
  'opted_out',
  'max_reminders',
  'expired',
  'manual',
  'failed',
] as const;
export type ReminderStopReason = (typeof REMINDER_STOP_REASONS)[number];

export const FEEDBACK_STATUSES = ['new', 'read', 'resolved'] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

/** Hard cap (ARCHITECTURE §8). The store setting can only lower it. */
export const MAX_REVIEW_REMINDERS = 3;

// ─── merge variables / templates ─────────────────────────────────────────────

/** Variables allowed in review templates. `{{link}}` is required in every message body. */
export const REVIEW_MERGE_VARS = ['firstName', 'businessName', 'link', 'senderName'] as const;
export type ReviewMergeVar = (typeof REVIEW_MERGE_VARS)[number];

const VAR_RE = /\{\{\s*([A-Za-z]+)\s*\}\}/g;

/** Variable names used in a template (unknown ones included, for validation). */
export function templateVariables(template: string): string[] {
  return [...template.matchAll(VAR_RE)].map((m) => m[1]!);
}

/** Remove control characters and collapse whitespace in a merge value (names come from imports). */
function cleanMergeValue(v: string | null | undefined, max = 60): string {
  if (!v) return '';
  return v
    .replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e]/g, ' ')
    .replace(/[{}<>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

const GREEK_CAPITAL_RE = /[\u0391-\u03a9]/;

/**
 * Render a review template. Safe fallbacks: a missing first name is dropped and the punctuation
 * around it tidied ("Γεια σας {{firstName}}!" → "Γεια σας!", "{{firstName}}, η γνώμη…" →
 * "Η γνώμη…"); unknown variables render as nothing. When `channel` is `sms` and the template is a
 * Greek-capitals GSM-7 text, merge values are converted too so the message stays GSM-7 (cheaper).
 */
export function renderReviewTemplate(
  template: string,
  vars: Partial<Record<ReviewMergeVar, string | null | undefined>>,
  opts: { channel?: ReviewChannel; locale?: 'el' | 'en' } = {},
): string {
  // {{firstName}} addresses the recipient: vocative in Greek ("Γεια σας Μάριε").
  if (opts.locale && vars.firstName)
    vars = { ...vars, firstName: addressFirstName(cleanMergeValue(vars.firstName), opts.locale) };
  const skeleton = template.replace(VAR_RE, '');
  const capitals = opts.channel === 'sms' && isGsm7(skeleton) && GREEK_CAPITAL_RE.test(skeleton);
  let out = template.replace(VAR_RE, (_m, name: string) => {
    if (!(REVIEW_MERGE_VARS as readonly string[]).includes(name)) return '';
    const raw = vars[name as ReviewMergeVar];
    // Links are never altered/truncated.
    if (name === 'link') return (raw ?? '').trim();
    const v = cleanMergeValue(raw);
    return capitals ? toGreekGsmCapitals(v) : v;
  });
  // Tidy what an empty value leaves behind, line by line (keep paragraph breaks in emails).
  out = out
    .split('\n')
    .map((line) => {
      let l = line.replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+([!,.;:?])/g, '$1');
      const stripped = l.replace(/^[ \t]*[,;:!][ \t]*/, '');
      if (stripped !== l) l = stripped.charAt(0).toLocaleUpperCase() + stripped.slice(1);
      return l.trim();
    })
    .join('\n');
  return out.trim();
}

// ─── Google review URL ───────────────────────────────────────────────────────

/**
 * Accepts the links Google gives businesses for collecting reviews:
 *  - https://g.page/r/<id>/review (Business Profile "Ask for reviews" share link)
 *  - https://search.google.com/local/writereview?placeid=<place id>
 *  - https://www.google.com/maps/… / https://maps.google.com/… (place page; review button on it)
 *  - https://maps.app.goo.gl/<code> (short Maps link)
 */
export function isGoogleReviewUrl(value: string): boolean {
  let u: URL;
  try {
    u = new URL(value.trim());
  } catch {
    return false;
  }
  if (u.protocol !== 'https:' || u.username || u.password || u.port) return false;
  const host = u.hostname.toLowerCase();
  const path = u.pathname;
  if (host === 'g.page') return path.length > 1;
  if (host === 'search.google.com')
    return path.startsWith('/local/writereview') && Boolean(u.searchParams.get('placeid'));
  if (host === 'maps.app.goo.gl') return path.length > 1;
  if (host === 'maps.google.com') return true;
  if (/^(www\.)?google\.[a-z]{2,3}(\.[a-z]{2})?$/.test(host)) return path.startsWith('/maps');
  return false;
}

export const GoogleReviewUrlSchema = z
  .string()
  .trim()
  .max(1000)
  .refine(isGoogleReviewUrl, { message: 'invalid_google_review_url' });

// ─── store settings (locations.review_settings) ──────────────────────────────

const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

const LocalizedText = z.object({ el: z.string().max(2000), en: z.string().max(2000) });
const text = (el: string, en: string) => LocalizedText.default({ el, en });

/**
 * Greek SMS defaults ship in GSM-7 "Greek capitals without accents" (ARCHITECTURE §7): with a
 * production-length link a typical message is 1 SMS credit instead of 3 in lowercase (UCS-2).
 * Merge values are converted to capitals too when rendered (renderReviewTemplate).
 */
export const REVIEW_DEFAULT_TEMPLATES = {
  sms: {
    el: toGreekGsmCapitals(
      'Γεια σας {{firstName}}! Ευχαριστούμε που επιλέξατε {{businessName}}. Πώς σας φάνηκε; {{link}}',
    ),
    en: 'Hi {{firstName}}! Thank you for choosing {{businessName}}. How was your experience? {{link}}',
  },
  reminderSms: {
    el: toGreekGsmCapitals(
      '{{firstName}}, η γνώμη σας μετράει για εμάς στο {{businessName}}. {{link}}',
    ),
    en: '{{firstName}}, your opinion matters to us at {{businessName}}. {{link}}',
  },
  emailSubject: {
    el: 'Πώς ήταν η επίσκεψή σας στο {{businessName}};',
    en: 'How was your visit to {{businessName}}?',
  },
  emailBody: {
    el: 'Γεια σας {{firstName}},\n\nΣας ευχαριστούμε για την επίσκεψή σας. Θα θέλαμε πολύ να μάθουμε πώς ήταν η εμπειρία σας. Μας παίρνει μόνο ένα λεπτό.\n\n{{link}}\n\nΜε εκτίμηση,\n{{senderName}}',
    en: 'Hi {{firstName}},\n\nThank you for visiting us. We would love to hear how your experience was. It only takes a minute.\n\n{{link}}\n\nKind regards,\n{{senderName}}',
  },
  reminderEmailSubject: {
    el: 'Μας λέτε τη γνώμη σας;',
    en: 'Would you share your opinion?',
  },
  reminderEmailBody: {
    el: 'Γεια σας {{firstName}},\n\nΑν έχετε ένα λεπτό, θα χαιρόμασταν να μάθουμε πώς σας φάνηκε η επίσκεψή σας στο {{businessName}}.\n\n{{link}}\n\n{{senderName}}',
    en: 'Hi {{firstName}},\n\nIf you have a minute, we would love to know how you found your visit to {{businessName}}.\n\n{{link}}\n\n{{senderName}}',
  },
} as const;

export const REVIEW_DEFAULT_PAGE_TEXTS = {
  question: { el: 'Πώς ήταν η εμπειρία σας;', en: 'How was your experience?' },
  likedMessage: {
    el: 'Χαιρόμαστε πολύ! Θα μας βοηθούσατε αφήνοντας μια κριτική στο Google.',
    en: 'We are so glad! You would help us a lot by leaving a Google review.',
  },
  dislikedMessage: {
    el: 'Λυπούμαστε που η εμπειρία σας δεν ήταν η αναμενόμενη. Πείτε μας τι συνέβη. Διαβάζουμε κάθε μήνυμα.',
    en: 'We are sorry your experience was not what you expected. Tell us what happened. We read every message.',
  },
  feedbackThanks: {
    el: 'Σας ευχαριστούμε. Το μήνυμά σας έφτασε απευθείας σε εμάς.',
    en: 'Thank you. Your message came straight to us.',
  },
} as const;

/** Logo stored inline (no third-party requests from the public page): small PNG/JPEG/WebP only. */
export const REVIEW_LOGO_MAX_CHARS = 200_000;
export const LogoDataUrlSchema = z
  .string()
  .max(REVIEW_LOGO_MAX_CHARS)
  .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/, { message: 'invalid_logo' });

export const BrandColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, { message: 'invalid_color' });

const TemplatesSchema = z
  .object({
    sms: text(REVIEW_DEFAULT_TEMPLATES.sms.el, REVIEW_DEFAULT_TEMPLATES.sms.en),
    reminderSms: text(
      REVIEW_DEFAULT_TEMPLATES.reminderSms.el,
      REVIEW_DEFAULT_TEMPLATES.reminderSms.en,
    ),
    emailSubject: text(
      REVIEW_DEFAULT_TEMPLATES.emailSubject.el,
      REVIEW_DEFAULT_TEMPLATES.emailSubject.en,
    ),
    emailBody: text(REVIEW_DEFAULT_TEMPLATES.emailBody.el, REVIEW_DEFAULT_TEMPLATES.emailBody.en),
    reminderEmailSubject: text(
      REVIEW_DEFAULT_TEMPLATES.reminderEmailSubject.el,
      REVIEW_DEFAULT_TEMPLATES.reminderEmailSubject.en,
    ),
    reminderEmailBody: text(
      REVIEW_DEFAULT_TEMPLATES.reminderEmailBody.el,
      REVIEW_DEFAULT_TEMPLATES.reminderEmailBody.en,
    ),
  })
  .prefault({});

const PageTextsSchema = z
  .object({
    question: text(REVIEW_DEFAULT_PAGE_TEXTS.question.el, REVIEW_DEFAULT_PAGE_TEXTS.question.en),
    likedMessage: text(
      REVIEW_DEFAULT_PAGE_TEXTS.likedMessage.el,
      REVIEW_DEFAULT_PAGE_TEXTS.likedMessage.en,
    ),
    dislikedMessage: text(
      REVIEW_DEFAULT_PAGE_TEXTS.dislikedMessage.el,
      REVIEW_DEFAULT_PAGE_TEXTS.dislikedMessage.en,
    ),
    feedbackThanks: text(
      REVIEW_DEFAULT_PAGE_TEXTS.feedbackThanks.el,
      REVIEW_DEFAULT_PAGE_TEXTS.feedbackThanks.en,
    ),
  })
  .prefault({});

const BASE_SETTINGS = z.object({
  channel: z.enum(REVIEW_CHANNEL_PREFERENCES).default('both_prefer_sms'),
  autoSendOnContactAdd: z.boolean().default(false),
  autoSendDelayHours: z.number().int().min(0).max(168).default(2),
  /** Server-managed: when auto-send was last switched on (older contacts are never auto-asked). */
  autoSendEnabledAt: IsoDateTimeSchema.nullable().default(null),
  remindersCount: z.number().int().min(0).max(MAX_REVIEW_REMINDERS).default(1),
  daysBetweenReminders: z.number().int().min(1).max(30).default(3),
  requestExpiryDays: z.number().int().min(1).max(90).default(30),
  /** Do not ask again a customer who clicked Google / sent feedback within this many days. */
  dedupeDays: z.number().int().min(0).max(365).default(90),
  quietHours: z
    .object({ start: TimeOfDay, end: TimeOfDay })
    .default({ start: '21:00', end: '09:00' }),
  templates: TemplatesSchema,
  pageTexts: PageTextsSchema,
  branding: z
    .object({
      logoDataUrl: LogoDataUrlSchema.nullable().default(null),
      brandColor: BrandColorSchema.nullable().default(null),
    })
    .prefault({}),
});

/** Stored shape — lenient (old rows parse with defaults). */
export const ReviewSettingsSchema = BASE_SETTINGS;
export type ReviewSettings = z.infer<typeof ReviewSettingsSchema>;

const BODY_TEMPLATES = ['sms', 'reminderSms', 'emailBody', 'reminderEmailBody'] as const;
const ALL_TEMPLATES = [
  ...BODY_TEMPLATES,
  'emailSubject',
  'reminderEmailSubject',
] as const satisfies readonly (keyof ReviewSettings['templates'])[];

/** Max SMS parts a review message may use (= messaging's MAX_SMS_SEGMENTS; sends above it fail). */
export const REVIEW_SMS_MAX_SEGMENTS = MAX_SMS_SEGMENTS;
/**
 * Link length budgeted when checking SMS templates: `https://<domain>/r/<43-char token>`.
 * Production (`https://app.growhero.gr/r/…`) is 69 chars; 80 leaves headroom for the domain.
 */
export const REVIEW_SMS_LINK_LENGTH = 80;
const SMS_TEMPLATES = ['sms', 'reminderSms'] as const;

/**
 * Worst-case SMS parts of a template: merge values at their maximum rendered length (60 chars —
 * see cleanMergeValue) as both GSM-7 text and non-GSM text (e.g. a Greek name in a Latin
 * template forces UCS-2), and a link of REVIEW_SMS_LINK_LENGTH chars.
 */
export function reviewSmsWorstCaseSegments(template: string): number {
  const link = `https://x/r/${'A'.repeat(Math.max(0, REVIEW_SMS_LINK_LENGTH - 12))}`;
  let worst = 0;
  for (const ch of ['x', 'ά']) {
    const v = ch.repeat(60);
    const rendered = renderReviewTemplate(
      template,
      { firstName: v, businessName: v, senderName: v, link },
      { channel: 'sms' },
    );
    worst = Math.max(worst, analyzeSms(rendered).segments);
  }
  return worst;
}

/** Validation issues of a settings object's templates (shared by API and the settings form). */
export function reviewTemplateIssues(
  templates: ReviewSettings['templates'],
): { path: string[]; message: string }[] {
  const issues: { path: string[]; message: string }[] = [];
  for (const key of ALL_TEMPLATES) {
    for (const lang of ['el', 'en'] as const) {
      const value = templates[key][lang];
      if (!value.trim()) {
        issues.push({ path: ['templates', key, lang], message: 'required' });
        continue;
      }
      const vars = templateVariables(value);
      const unknown = vars.filter((v) => !(REVIEW_MERGE_VARS as readonly string[]).includes(v));
      if (unknown.length)
        issues.push({ path: ['templates', key, lang], message: `unknown_variable:${unknown[0]}` });
      const isBody = (BODY_TEMPLATES as readonly string[]).includes(key);
      if (isBody && !vars.includes('link'))
        issues.push({ path: ['templates', key, lang], message: 'missing_link' });
      if (!isBody && vars.includes('link'))
        issues.push({ path: ['templates', key, lang], message: 'link_not_allowed_in_subject' });
      // Messaging refuses SMS above REVIEW_SMS_MAX_SEGMENTS parts: every send would fail.
      if (
        (SMS_TEMPLATES as readonly string[]).includes(key) &&
        reviewSmsWorstCaseSegments(value) > REVIEW_SMS_MAX_SEGMENTS
      )
        issues.push({ path: ['templates', key, lang], message: 'sms_too_long' });
    }
  }
  return issues;
}

/** Strict input shape for PUT /reviews/settings. */
export const ReviewSettingsInputSchema = BASE_SETTINGS.superRefine((v, ctx) => {
  for (const i of reviewTemplateIssues(v.templates))
    ctx.addIssue({ code: 'custom', path: i.path, message: i.message });
  for (const [k, t] of Object.entries(v.pageTexts))
    for (const lang of ['el', 'en'] as const)
      if (!t[lang].trim())
        ctx.addIssue({ code: 'custom', path: ['pageTexts', k, lang], message: 'required' });
});

export function defaultReviewSettings(): ReviewSettings {
  return ReviewSettingsSchema.parse({});
}

/** Parse a stored jsonb value, falling back to defaults field by field. */
/**
 * Earlier default texts that tenants still have stored verbatim (they contained em dashes,
 * ADR-UI-A3). A stored value that equals one of these exactly is the untouched old default,
 * so it is read as today's default; anything the owner edited is kept as written.
 */
const LEGACY_REVIEW_DEFAULTS: {
  group: 'templates' | 'pageTexts';
  key: string;
  el: string;
  en: string;
}[] = [
  {
    group: 'templates',
    key: 'emailBody',
    el: 'Γεια σας {{firstName}},\n\nΣας ευχαριστούμε για την επίσκεψή σας. Θα θέλαμε πολύ να μάθουμε πώς ήταν η εμπειρία σας \u2014 μας παίρνει μόνο ένα λεπτό.\n\n{{link}}\n\nΜε εκτίμηση,\n{{senderName}}',
    en: 'Hi {{firstName}},\n\nThank you for visiting us. We would love to hear how your experience was \u2014 it only takes a minute.\n\n{{link}}\n\nKind regards,\n{{senderName}}',
  },
  {
    group: 'pageTexts',
    key: 'dislikedMessage',
    el: 'Λυπούμαστε που η εμπειρία σας δεν ήταν η αναμενόμενη. Πείτε μας τι συνέβη \u2014 διαβάζουμε κάθε μήνυμα.',
    en: 'We are sorry your experience was not what you expected. Tell us what happened \u2014 we read every message.',
  },
];

function upgradeLegacyDefaults(s: ReviewSettings): ReviewSettings {
  let out = s;
  for (const d of LEGACY_REVIEW_DEFAULTS) {
    const cur = (out[d.group] as Record<string, { el: string; en: string }>)[d.key]!;
    const defaults = (
      d.group === 'templates' ? REVIEW_DEFAULT_TEMPLATES : REVIEW_DEFAULT_PAGE_TEXTS
    ) as Record<string, { el: string; en: string }>;
    const next = {
      el: cur.el === d.el ? defaults[d.key]!.el : cur.el,
      en: cur.en === d.en ? defaults[d.key]!.en : cur.en,
    };
    if (next.el !== cur.el || next.en !== cur.en)
      out = { ...out, [d.group]: { ...out[d.group], [d.key]: next } };
  }
  return out;
}

export function parseStoredReviewSettings(raw: unknown): ReviewSettings {
  return upgradeLegacyDefaults(parseStoredReviewSettingsRaw(raw));
}

function parseStoredReviewSettingsRaw(raw: unknown): ReviewSettings {
  const r = ReviewSettingsSchema.safeParse(raw ?? {});
  if (r.success) return r.data;
  // Corrupt/legacy fields: keep what parses, default the rest.
  const base = defaultReviewSettings();
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const out: Record<string, unknown> = { ...base };
  for (const key of Object.keys(base)) {
    const one = BASE_SETTINGS.pick({ [key]: true } as never).safeParse({ [key]: obj[key] });
    if (one.success) out[key] = (one.data as Record<string, unknown>)[key];
  }
  return out as ReviewSettings;
}

// ─── locations / store API ───────────────────────────────────────────────────

const TimezoneSchema = z
  .string()
  .min(1)
  .max(64)
  .refine(
    (tz) => {
      try {
        new Intl.DateTimeFormat('en', { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    },
    { message: 'invalid_timezone' },
  );

/** SMS alphanumeric sender IDs: max 11 Latin letters/digits (spaces, . - & allowed). */
export const SenderNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(11)
  .regex(/^[A-Za-z0-9][A-Za-z0-9 .&-]*$/, { message: 'invalid_sender_name' });

export const ReviewLocationSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  googleReviewUrl: z.string().nullable(),
  senderName: z.string().nullable(),
  timezone: z.string(),
  isDefault: z.boolean(),
});
export type ReviewLocation = z.infer<typeof ReviewLocationSchema>;

export const ReviewStoreResponseSchema = z.object({
  location: ReviewLocationSchema,
  settings: ReviewSettingsSchema,
  businessName: z.string(),
  /** Base URL used in /r/<token> links (for previews). */
  publicLinkBase: z.string(),
  limits: z.object({ stores: z.number().int(), used: z.number().int() }),
  /** What still blocks sending (empty = ready). */
  missing: z.array(z.enum(['google_review_url', 'sender_name'])),
});
export type ReviewStoreResponse = z.infer<typeof ReviewStoreResponseSchema>;

export const UpdateReviewStoreRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  address: z.string().trim().max(300).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  googleReviewUrl: GoogleReviewUrlSchema.nullable(),
  senderName: SenderNameSchema.nullable(),
  timezone: TimezoneSchema.optional(),
  settings: ReviewSettingsInputSchema,
});
export type UpdateReviewStoreRequest = z.input<typeof UpdateReviewStoreRequestSchema>;

export const CreateLocationRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  address: z.string().trim().max(300).nullable().optional(),
});

// ─── review requests ─────────────────────────────────────────────────────────

export const REVIEW_SKIP_REASONS = [
  'active_request',
  'recently_completed',
  'opted_out',
  'no_channel',
  'contact_not_found',
] as const;
export type ReviewSkipReason = (typeof REVIEW_SKIP_REASONS)[number];

export const CreateReviewRequestsSchema = z
  .object({
    contactIds: z.array(UuidSchema).max(500).optional(),
    /** Every active contact that has never received a review request (capped at 500). */
    allNew: z.boolean().optional(),
    /** Force a channel (otherwise the store's channel policy decides per contact). */
    channel: z.enum(REVIEW_CHANNELS).optional(),
  })
  .refine((v) => Boolean(v.allNew) || (v.contactIds?.length ?? 0) > 0, {
    message: 'contactIds or allNew is required',
    path: ['contactIds'],
  });
export type CreateReviewRequestsInput = z.infer<typeof CreateReviewRequestsSchema>;

export const CreateReviewRequestsResponseSchema = z.object({
  created: z.array(
    z.object({
      id: UuidSchema,
      contactId: UuidSchema,
      channel: z.enum(REVIEW_CHANNELS),
      status: z.enum(REVIEW_REQUEST_STATUSES),
      scheduledFor: z.string().nullable(),
    }),
  ),
  skipped: z.array(z.object({ contactId: UuidSchema, reason: z.enum(REVIEW_SKIP_REASONS) })),
});
export type CreateReviewRequestsResponse = z.infer<typeof CreateReviewRequestsResponseSchema>;

export const ReviewRequestContactSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  firstName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
});

export const ReviewRequestSchema = z.object({
  id: UuidSchema,
  contact: ReviewRequestContactSchema,
  channel: z.enum(REVIEW_CHANNELS),
  status: z.enum(REVIEW_REQUEST_STATUSES),
  sentiment: z.enum(REVIEW_SENTIMENTS).nullable(),
  source: z.enum(REVIEW_REQUEST_SOURCES),
  locale: LocaleSchema,
  scheduledFor: z.string().nullable(),
  firstSentAt: z.string().nullable(),
  lastSentAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  openedAt: z.string().nullable(),
  sentimentAt: z.string().nullable(),
  /** Clicked the Google button. NOT a confirmed/posted review. */
  googleClickedAt: z.string().nullable(),
  feedbackAt: z.string().nullable(),
  optedOutAt: z.string().nullable(),
  remindersSent: z.number().int(),
  nextReminderAt: z.string().nullable(),
  stoppedReason: z.enum(REMINDER_STOP_REASONS).nullable(),
  expiresAt: z.string(),
  createdAt: z.string(),
  lastDeliveryStatus: z.string().nullable(),
});
export type ReviewRequest = z.infer<typeof ReviewRequestSchema>;

export const ReviewRequestEventSchema = z.object({
  id: UuidSchema,
  type: z.enum(REVIEW_REQUEST_EVENT_TYPES),
  meta: z.record(z.string(), z.unknown()),
  occurredAt: z.string(),
});

export const ReviewRequestDetailSchema = ReviewRequestSchema.extend({
  events: z.array(ReviewRequestEventSchema),
  deliveries: z.array(
    z.object({
      id: UuidSchema,
      channel: z.enum(REVIEW_CHANNELS),
      purpose: z.string(),
      status: z.string(),
      body: z.string().nullable(),
      subject: z.string().nullable(),
      createdAt: z.string(),
    }),
  ),
  feedback: z
    .object({ id: UuidSchema, message: z.string(), wantsContact: z.boolean(), status: z.string() })
    .nullable(),
});
export type ReviewRequestDetail = z.infer<typeof ReviewRequestDetailSchema>;

export const ReviewRequestListQuerySchema = z.object({
  status: z.enum(REVIEW_REQUEST_STATUSES).optional(),
  /** Only requests whose reminders are still running. */
  active: z.enum(['true', 'false']).optional(),
  q: z.string().trim().max(100).optional(),
  cursor: z.string().regex(/^\d+$/).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const ReviewRequestListResponseSchema = z.object({
  items: z.array(ReviewRequestSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int(),
});

/** Contacts as candidates for a review request (with the dedupe verdict). */
export const ReviewCandidateSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  createdAt: z.string(),
  lastRequestAt: z.string().nullable(),
  lastRequestStatus: z.enum(REVIEW_REQUEST_STATUSES).nullable(),
  eligible: z.boolean(),
  reason: z.enum(REVIEW_SKIP_REASONS).nullable(),
  channel: z.enum(REVIEW_CHANNELS).nullable(),
});
export type ReviewCandidate = z.infer<typeof ReviewCandidateSchema>;

export const ReviewCandidatesResponseSchema = z.object({
  items: z.array(ReviewCandidateSchema),
  newCount: z.number().int(),
});

// ─── stats ───────────────────────────────────────────────────────────────────

export const ReviewStatsQuerySchema = z.object({
  days: z.coerce.number().int().min(7).max(365).default(30),
});

/** Honest funnel: every number is a recorded event; clicks are never reviews. */
export const ReviewStatsSchema = z.object({
  days: z.number().int(),
  funnel: z.object({
    requested: z.number().int(),
    sent: z.number().int(),
    delivered: z.number().int(),
    opened: z.number().int(),
    liked: z.number().int(),
    disliked: z.number().int(),
    googleClicked: z.number().int(),
    privateFeedback: z.number().int(),
    optedOut: z.number().int(),
    failed: z.number().int(),
    scheduled: z.number().int(),
  }),
  /** Daily counts in the store's timezone (YYYY-MM-DD). */
  trend: z.array(
    z.object({
      date: z.string(),
      sent: z.number().int(),
      opened: z.number().int(),
      googleClicked: z.number().int(),
    }),
  ),
  feedback: z.object({ new: z.number().int(), total: z.number().int() }),
  active: z.number().int(),
});
export type ReviewStats = z.infer<typeof ReviewStatsSchema>;

// ─── private feedback inbox ──────────────────────────────────────────────────

export const PrivateFeedbackItemSchema = z.object({
  id: UuidSchema,
  reviewRequestId: UuidSchema.nullable(),
  contact: ReviewRequestContactSchema.nullable(),
  sentiment: z.enum(REVIEW_SENTIMENTS).nullable(),
  message: z.string(),
  wantsContact: z.boolean(),
  status: z.enum(FEEDBACK_STATUSES),
  readAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  createdAt: z.string(),
  /** Did the same customer also click the Google button? (a click, not a review) */
  googleClicked: z.boolean(),
  replies: z.number().int(),
});
export type PrivateFeedbackItem = z.infer<typeof PrivateFeedbackItemSchema>;

export const FeedbackListQuerySchema = z.object({
  status: z.enum(FEEDBACK_STATUSES).optional(),
  cursor: z.string().regex(/^\d+$/).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const FeedbackListResponseSchema = z.object({
  items: z.array(PrivateFeedbackItemSchema),
  nextCursor: z.string().nullable(),
  counts: z.object({ new: z.number().int(), read: z.number().int(), resolved: z.number().int() }),
});

export const UpdateFeedbackRequestSchema = z.object({ status: z.enum(FEEDBACK_STATUSES) });

export const FeedbackReplyRequestSchema = z.object({
  channel: z.enum(REVIEW_CHANNELS).optional(),
  subject: z.string().trim().max(200).optional(),
  body: z.string().trim().min(1).max(1600),
});

export const FeedbackReplyResponseSchema = z.object({
  deliveryId: UuidSchema,
  status: z.string(),
  channel: z.enum(REVIEW_CHANNELS),
  skippedReason: z.string().nullable(),
});

// ─── public review page (/r/:token) ──────────────────────────────────────────

/** Opaque public tokens: 32 bytes base64url = 43 chars. */
export const ReviewTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);

/**
 * Landing data. No PII beyond the first name. `googleReviewUrl` is present regardless of the
 * sentiment state — no review gating (Google policy, ARCHITECTURE §8).
 */
export const PublicReviewLandingSchema = z.object({
  businessName: z.string(),
  locationName: z.string(),
  locale: LocaleSchema,
  firstName: z.string().nullable(),
  googleReviewUrl: z.string().nullable(),
  texts: z.object({
    question: z.string(),
    likedMessage: z.string(),
    dislikedMessage: z.string(),
    feedbackThanks: z.string(),
  }),
  branding: z.object({ logoDataUrl: z.string().nullable(), brandColor: z.string().nullable() }),
  state: z.object({
    opened: z.boolean(),
    sentiment: z.enum(REVIEW_SENTIMENTS).nullable(),
    googleClicked: z.boolean(),
    feedbackSubmitted: z.boolean(),
    remindersStopped: z.boolean(),
    expired: z.boolean(),
  }),
});
export type PublicReviewLanding = z.infer<typeof PublicReviewLandingSchema>;

export const PublicSentimentRequestSchema = z.object({ sentiment: z.enum(REVIEW_SENTIMENTS) });

export const PublicSentimentResponseSchema = z.object({
  sentiment: z.enum(REVIEW_SENTIMENTS),
  message: z.string(),
  /** Always present when configured — for BOTH sentiments (no review gating). */
  googleReviewUrl: z.string().nullable(),
  feedbackAvailable: z.boolean(),
});

export const PublicFeedbackRequestSchema = z.object({
  message: z.string().trim().min(1).max(4000),
  wantsContact: z.boolean().default(false),
});

export const PublicGoogleClickResponseSchema = z.object({ url: z.string() });

export const PublicOkResponseSchema = z.object({ ok: z.literal(true) });

/** Google Business Profile matching states — never "confirmed". */
export const REVIEW_MATCH_STATES = [
  'invitation_sent',
  'link_clicked',
  'self_reported_completed',
  'public_review_detected',
  'possible_match',
  'review_identity_unverified',
] as const;
export type ReviewMatchState = (typeof REVIEW_MATCH_STATES)[number];

/**
 * Derived "furthest state" for lists/filters. The milestone timestamps stay the truth; a Google
 * click outranks everything because it is the strongest recorded action (still not a review).
 */
export function deriveReviewStatus(r: {
  googleClickedAt?: unknown;
  feedbackAt?: unknown;
  optedOutAt?: unknown;
  sentiment?: ReviewSentiment | null;
  openedAt?: unknown;
  expired?: boolean;
  failed?: boolean;
  deliveredAt?: unknown;
  firstSentAt?: unknown;
  queued?: boolean;
}): ReviewRequestStatus {
  if (r.googleClickedAt) return 'google_clicked';
  if (r.feedbackAt) return 'feedback_submitted';
  if (r.optedOutAt) return 'opted_out';
  if (r.sentiment) return r.sentiment;
  if (r.openedAt) return 'opened';
  if (r.expired) return 'expired';
  if (r.failed) return 'failed';
  if (r.deliveredAt) return 'delivered';
  if (r.firstSentAt) return 'sent';
  if (r.queued) return 'queued';
  return 'created';
}
